// API endpoints that do not require a live user session. These calls use
// baseFetch directly — a 401 here means "not signed in," not "session died."

import { ApiFetchError, baseFetch } from './baseFetch';
import {
  GameLibraryConfig,
  PlayerDocument,
  PlayerFieldsUpdate,
  RoomDocument,
  LiveStreamer,
  MaintenanceBannerState,
  ProductDocument,
  CampaignStatsResponse,
  UserAuthFields,
  AchievementTrackProgress,
} from '@airhead-games/shared/types';
import type { AccessTier } from '@airhead-games/shared/utilities';
import { getBaseUrl } from '@airhead-games/shared/utilities';

// GAMES
// Cookie is included so the Poppy admin account receives admin-only games.
// A signed-out request returns the standard tier; see backend gameHandlers.
export async function getAllGames() {
  try {
    return await baseFetch<
      undefined,
      { games: GameLibraryConfig[]; accessTier: AccessTier }
    >('/games', {
      method: 'GET',
      credentials: 'include',
    });
  } catch (error) {
    if (
      error instanceof ApiFetchError &&
      (error.status === 401 || error.status === 403)
    ) {
      return baseFetch<
        undefined,
        { games: GameLibraryConfig[]; accessTier: AccessTier }
      >('/games', {
        method: 'GET',
        credentials: 'omit',
      });
    }

    throw error;
  }
}

// ACHIEVEMENTS
// Cookie is included so a signed-in user gets their progress. A guest (or an
// expired session) gets the live tracks at zero; see backend achievementHandlers.
export function getAchievementProgress() {
  return baseFetch<undefined, { tracks: AchievementTrackProgress[] }>(
    '/achievements',
    { method: 'GET', credentials: 'include' }
  );
}

// USER SESSION PROBE
// Returns the signed-in user or 401 if there is no session. Callers should
// treat 401 as "guest" — not as session death (that's protectedFetch's job).
export function getUserDetails() {
  return baseFetch<undefined, { user: UserAuthFields }>('/user-details', {
    method: 'GET',
    credentials: 'include',
  });
}

// USER & AUTH
export function requestOTP(email: string) {
  return baseFetch<{ email: string }, { message: string }>('/otp-request', {
    method: 'POST',
    body: { email },
    credentials: 'omit',
  });
}

export function verifyOTP(email: string, otp: string) {
  return baseFetch<{ email: string; otp: string }, { message: string }>(
    '/otp-verify',
    {
      method: 'POST',
      body: { email, otp },
      credentials: 'include', // needed to set cookie
    }
  );
}

// PLAYERS
export function getPlayerDetails(params: {
  playerId?: string;
  twitchId?: string;
  sessionId?: string;
}) {
  const queryParams = new URLSearchParams();
  if (params.playerId) queryParams.append('playerId', params.playerId);
  if (params.twitchId) queryParams.append('twitchId', params.twitchId);
  if (params.sessionId) queryParams.append('sessionId', params.sessionId);

  return baseFetch<undefined, { player: PlayerDocument }>(
    `/player-details?${queryParams.toString()}`,
    {
      method: 'GET',
      credentials: 'omit',
    }
  );
}

export function initializePlayer(params: {
  sessionId: string;
  twitchId?: string;
  twitchPlayersOnly: boolean;
}) {
  return baseFetch<
    { sessionId: string; twitchId?: string; twitchPlayersOnly: boolean },
    { player: PlayerDocument }
  >('/player-initialize', {
    method: 'POST',
    body: params,
    credentials: 'omit',
  });
}

export function updatePlayer(
  playerId: string,
  update: PlayerFieldsUpdate & { roomCode?: string }
) {
  return baseFetch<
    PlayerFieldsUpdate & { roomCode?: string },
    { player: PlayerDocument }
  >(`/player-update/${playerId}`, {
    method: 'PATCH',
    body: update,
    credentials: 'omit',
  });
}

// ROOMS
// Doesn't use baseFetch because it's a HEAD request
export async function verifyRoomExists(roomCode: string): Promise<boolean> {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/api/room-exists/${roomCode}`, {
    method: 'HEAD',
    credentials: 'omit',
  });
  return response.ok;
}

export function getRoomDetails(roomCode: string) {
  return baseFetch<undefined, { room: RoomDocument }>(
    `/room-details/${roomCode}`,
    {
      method: 'GET',
      credentials: 'omit',
    }
  );
}

// TWITCH
export function getLiveNowData() {
  return baseFetch<
    undefined,
    {
      liveStreamers: LiveStreamer[];
      count: number;
      lastUpdated: number;
    }
  >('/twitch/live-now', {
    method: 'GET',
    credentials: 'omit',
  });
}

export function initiateTwitchAuth(
  intent: 'login' | 'link' | 'player' = 'login',
  options?: { playerId?: string; roomCode?: string; redirectTo?: string }
) {
  // This redirects to Twitch, so we use window.location.href instead of fetch
  const baseUrl = getBaseUrl();
  const url = new URL(`${baseUrl}/api/auth/twitch`);
  url.searchParams.set('intent', intent);
  if (intent === 'player') {
    if (options?.playerId) {
      url.searchParams.set('playerId', options.playerId);
    }
    if (options?.roomCode) {
      url.searchParams.set('roomCode', options.roomCode);
    }
  }
  if (options?.redirectTo) {
    url.searchParams.set('redirectTo', options.redirectTo);
  }
  window.location.href = url.toString();
}

// PRODUCTS
export function getAllActiveProducts() {
  return baseFetch<undefined, { products: ProductDocument[] }>('/products', {
    method: 'GET',
    credentials: 'omit',
  });
}

// CAMPAIGNS
export function getCampaignStats(campaignId: string) {
  return baseFetch<undefined, CampaignStatsResponse>(
    `/campaigns/${campaignId}/stats`,
    {
      method: 'GET',
      credentials: 'omit',
    }
  );
}

// STATE
export function getMaintenanceBanner() {
  return baseFetch<undefined, MaintenanceBannerState>(
    '/state/maintenance-banner',
    {
      method: 'GET',
      credentials: 'omit',
    }
  );
}

// DRAWINGS
// Anonymous like the websocket command path; room saves are gated server-side
// by the same room/player/window/ownership validators as PlayerInput commands.

interface DrawingSaveResult {
  fileName: string;
  signedUrl: string;
}

interface RecentDrawingResult {
  fileName: string;
  signedUrl: string;
  title?: string;
  from?: string;
}

interface DrawingUploadBody {
  imageBase64: string;
  mimeType: string;
  title?: string;
  from?: string;
}

// FileReader keeps large blobs off the call stack (vs. btoa over a byte array)
function blobToBase64(image: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(image);
  });
}

interface DrawingMetadata {
  title?: string;
  from?: string;
}

export async function getRecentDrawings() {
  return baseFetch<undefined, { drawings: RecentDrawingResult[] }>(
    '/drawings/recent',
    {
      method: 'GET',
      credentials: 'omit',
    }
  );
}

// Save a drawing with no room context (standalone /draw page)
export async function submitDrawing(
  image: Blob,
  metadata: DrawingMetadata = {}
) {
  const imageBase64 = await blobToBase64(image);
  const title = metadata.title?.trim();
  const from = metadata.from?.trim();

  return baseFetch<DrawingUploadBody, DrawingSaveResult>('/drawings', {
    method: 'POST',
    body: {
      imageBase64,
      mimeType: image.type,
      ...(title ? { title } : {}),
      ...(from ? { from } : {}),
    },
    credentials: 'omit',
  });
}
